import React from 'react';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import { Grid, Typography, TextField, RadioGroup, Radio, FormControlLabel, IconButton, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * AdverseEventRelevanceItem (세트 단위 인과관계 평가)
 *
 * props:
 * - basePath: string (예: drugInfos.0.adverseEventRelevance.0)
 * - onRemove: () => void (상위 관련성 카드 삭제)
 * - index: number (옵션)
 * - repeatBoxSx, closeBtnSx, firstColSx: 스타일 오버라이드(옵션)
 *
 * 데이터 구조(권장):
 * adverseEventRelevance[].causalAssessmentSets: Array<{
 *   whoUmc: { type:"who-umc", result:"" },
 *   other:  { type:"other", methodOther:"", resultOther:"" }
 * }>
 */
export default function AdverseEventRelevanceItem({
  basePath,
  onRemove,
  index,
  repeatBoxSx: repeatBoxSxProp,
  closeBtnSx: closeBtnSxProp,
  firstColSx: firstColSxProp,
}) {
  const { control } = useFormContext();

  // 기본 스타일
  const repeatBoxSx = repeatBoxSxProp || {
    mb: 1.5,
    p: 2,
    pr: 8,
    border: '1px solid',
    borderRadius: 1,
    position: 'relative',
    backgroundColor: 'background.paper',
    overflow: 'visible',
  };
  const closeBtnSx = closeBtnSxProp || {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 36,
    height: 36,
    color: 'error.main',
    zIndex: 3,
    borderRadius: 1,
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: -4,
      backgroundColor: 'background.paper',
      borderRadius: 1,
      zIndex: -1,
      boxShadow: '0 0 0 1px rgba(0,0,0,0.06)',
    },
    '& svg': { fontSize: 20 },
    '&:hover': { bgcolor: 'rgba(244,67,54,0.08)', color: 'error.dark' },
  };
  const firstColSx = firstColSxProp || { mt: { xs: 5, md: 0 } };

  // 세트 배열: 각 세트가 who-umc + other를 함께 포함
  const {
    fields: setFields,
    append: setAppend,
    remove: setRemove,
  } = useFieldArray({ control, name: `${basePath}.causalAssessmentSets` });

  return (
    <Grid container spacing={1} sx={{ ...repeatBoxSx, border: '1px solid #7CB8FF' }}>
      {/* 상단 빨간 X (관련성 카드 삭제) */}
      <IconButton onClick={onRemove} aria-label="관련성 항목 삭제" sx={closeBtnSx}>
        <CloseIcon />
      </IconButton>

      {/* 평가대상 증상명 */}
      <Grid size={{ xs: 12, md: 6 }} sx={firstColSx}>
        <Controller
          name={`${basePath}.symptomName`}
          control={control}
          defaultValue=""
          render={({ field }) => <TextField {...field} label="평가대상 증상명" fullWidth />}
        />
      </Grid>

      {/* 재투여 후 반응 재발 여부 */}
      <Grid size={{ xs: 12 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          재투여 후 반응 재발 여부
        </Typography>
        <Controller
          name={`${basePath}.rechallengeOutcome`}
          control={control}
          defaultValue=""
          render={({ field }) => (
            <RadioGroup row {...field}>
              <FormControlLabel value="recurrence" control={<Radio />} label="재투여하고 이상반응이 다시 나타남" />
              <FormControlLabel value="noRecurrence" control={<Radio />} label="재투여했지만 이상반응 나타나지 않음" />
              <FormControlLabel value="unknown" control={<Radio />} label="재투여했지만 결과는 알 수 없음" />
              <FormControlLabel value="notDone" control={<Radio />} label="재투여하지 않아 해당사항 없음" />
            </RadioGroup>
          )}
        />
      </Grid>

      {/* 인과관계 평가 (세트: WHO-UMC + 기타 한 박스) */}
      <Grid size={{ xs: 12 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          인과관계 평가 (복수 작성 가능)
        </Typography>
      </Grid>

      {setFields.map((row, si) => {
        const sn = `${basePath}.causalAssessmentSets.${si}`;
        return (
          <Grid container spacing={2} key={row.id} sx={{ ...repeatBoxSx, border: '1px solid #7986cb' }}>
            {/* 세트 삭제 (한 번에 삭제) */}
            <IconButton onClick={() => setRemove(si)} aria-label="인과관계 평가 세트 삭제" sx={closeBtnSx}>
              <CloseIcon />
            </IconButton>

            {/* 세트 본문: WHO-UMC + 기타를 한 박스에 배치 */}
            {/* WHO-UMC (좌측) */}
            <Grid size={{ xs: 12 }} sx={firstColSx}>
              {/* 평가방법 (고정 문구) */}
              <TextField
                value="WHO-UMC 인과관계 평가"
                label="평가방법"
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{ mb: 1 }}
              />

              {/* 평가결과 (라디오) */}
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                평가결과
              </Typography>
              <Controller
                name={`${sn}.whoUmc.result`}
                control={control}
                defaultValue={row?.whoUmc?.result ?? ''}
                render={({ field }) => (
                  <RadioGroup row {...field}>
                    <FormControlLabel value="certain" control={<Radio />} label="확실함(Certain)" />
                    <FormControlLabel value="probable" control={<Radio />} label="상당히 확실함(Probable/likely)" />
                    <FormControlLabel value="possible" control={<Radio />} label="가능함(Possible)" />
                    <FormControlLabel value="unlikely" control={<Radio />} label="가능성 적음(Unlikely)" />
                    <FormControlLabel
                      value="conditional"
                      control={<Radio />}
                      label="평가 곤란(Conditional/unclassified)"
                    />
                    <FormControlLabel
                      value="unassessable"
                      control={<Radio />}
                      label="평가 불가(Unassessable/unclassifiable)"
                    />
                  </RadioGroup>
                )}
              />
            </Grid>

            {/* 기타 (우측) */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                기타 평가
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }} container>
              <Grid size={{ xs: 6 }}>
                {/* 평가방법(기타) */}
                <Controller
                  name={`${sn}.other.methodOther`}
                  control={control}
                  defaultValue={row?.other?.methodOther ?? ''}
                  render={({ field }) => <TextField {...field} label="평가방법(기타)" fullWidth sx={{ mb: 1 }} />}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                {/* 평가결과(기타) */}
                <Controller
                  name={`${sn}.other.resultOther`}
                  control={control}
                  defaultValue={row?.other?.resultOther ?? ''}
                  render={({ field }) => <TextField {...field} label="평가결과(기타)" fullWidth />}
                />
              </Grid>
            </Grid>
          </Grid>
        );
      })}

      {/* 평가 세트 추가: 클릭 시 WHO-UMC + 기타를 함께 포함한 세트를 한 번에 추가 */}
      <Grid size={{ xs: 12 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() =>
            setAppend({
              whoUmc: { type: 'who-umc', result: '' },
              other: { type: 'other', methodOther: '', resultOther: '' },
            })
          }
          sx={{ backgroundColor: '#7986cb', color: 'white' }}
        >
          평가 세트 추가 (WHO-UMC + 기타)
        </Button>
      </Grid>
    </Grid>
  );
}
